/**
 * Copyright 2025 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import { BASE_VIEW_CONFIG, VIEW_STATE_RULES } from "./consts";
import type { ResourceViewState, ViewConfig, ViewStateParams } from "./types";

/**
 * Determines the current view state by finding the first matching rule in `VIEW_STATE_RULES`.
 * @param context - The current application state context.
 * @returns The name of the current view state (e.g., "content").
 */
export function deriveViewState(context: ViewStateParams): ResourceViewState {
  const rule = VIEW_STATE_RULES.find(({ when }) => when(context));
  return rule ? rule.state : "waiting";
}

/**
 * Determines the final UI configuration (e.g., `showLoader`).
 * It takes the base config for the current state and applies specific overrides
 * based on the context (e.g., showing a loader during a refetch).
 * @param viewState - The name of the current view state.
 * @param context - The current application state context.
 * @returns The final view configuration object for the UI.
 */
export function deriveViewConfig(
  viewState: ResourceViewState,
  context: ViewStateParams
): ViewConfig {
  const { isRefetching } = context;
  const base = BASE_VIEW_CONFIG[viewState];

  if (viewState === "content") {
    return {
      ...base,
      showLoader: base.showLoader || isRefetching,
      showLoadMore: base.showLoadMore && !isRefetching,
    };
  }

  if (isRefetching && viewState !== "searchHint") {
    return {
      ...base,
      showLoader: true,
    };
  }

  return base;
}
